import { useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'

import {
  type HoleScoreFragment,
  useCurrentPlayerFlightScoresQuery,
} from '../../__generated__/hooks'
import type { CurrentPlayerCtx } from '../../PlayerShell'
import { isBestBallFlight } from '../../util/best-ball'
import BestBallView from './views/BestBallView'
import IndividualView from './views/IndividualView'

/**
 * Traditional Norwegian scorecard, rebuilt for mobile.
 *
 * The printed A5-landscape card has holes across columns and players
 * down rows. On a phone that means horizontal scroll past hole 9, with
 * tiny cells that can't carry proper hand-drawn score marks. We flip
 * the axis: holes go down (vertical scroll is the mobile-native axis),
 * players go across. Marks render at ink-size (40–48px); the card fits
 * any phone width without horizontal pan.
 *
 * Mark grammar follows the broadcast/TV convention (see
 * `components/ScoreMark.tsx`) rather than the Norwegian paper
 * convention — better visual hierarchy on a phone.
 *
 * This dispatcher narrows on event mode (via flight shape) and forwards
 * to the variant view. Data lives in the Apollo cache from Score; this
 * tab is a pure presentation surface — no mutations, no polling beyond
 * cache-and-network.
 */
const Scorecard = () => {
  const { player } = useOutletContext<{ player: CurrentPlayerCtx }>()
  const { data, loading, error } = useCurrentPlayerFlightScoresQuery({
    fetchPolicy: 'cache-and-network',
    // Read-only review surface, but the scores + per-member hcp behind it keep
    // changing (flight members entering, admin corrections). Poll so the card
    // stays truthful without a reload.
    pollInterval: 15_000,
  })

  // Memoised so child hooks don't re-fire every render on the freshly-
  // created `?? []` empty array.
  const flight = useMemo(
    () => data?.currentPlayerFlightScores ?? [],
    [data?.currentPlayerFlightScores]
  )
  const holes = useMemo<HoleScoreFragment[]>(
    () =>
      (flight[0]?.scores ?? [])
        .slice()
        .sort((a, b) => a.hole.number - b.hole.number),
    [flight]
  )

  if (loading && !data) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster scorekort…
      </section>
    )
  }
  if (error || !flight.length || !holes.length) {
    return (
      <section className="px-4 py-8 text-center text-flag">
        Fant ikke scorekortet ditt.
      </section>
    )
  }

  if (isBestBallFlight(flight)) {
    return <BestBallView player={player} flight={flight} holes={holes} />
  }
  return <IndividualView player={player} flight={flight} holes={holes} />
}

export default Scorecard
